import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/app/_module/lib/utils';
import { FactGrid, StatusLabel, displayFont, toneWash, type Fact, type Tone } from './primitives';

const SURFACE =
  'relative overflow-hidden rounded-xl border border-neutral-200/80 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_16px_40px_-24px_rgba(16,24,40,0.18)]';

/**
 * The focal point of the tab: where the registration stands and the one thing
 * to do next. The faint wash at the top carries the status colour, and this is
 * the only surface on the page with a shadow.
 */
export function StatusPanel({
  tone,
  label,
  title,
  children,
  detail,
  actions,
  facts,
}: {
  tone: Tone;
  label: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  /** Sits between the copy and the actions: a code form, a quoted reason. */
  detail?: React.ReactNode;
  actions?: React.ReactNode;
  facts?: Fact[];
}) {
  return (
    <section aria-labelledby="a2p-status-title" className={SURFACE}>
      <div
        aria-hidden
        className={cn('pointer-events-none absolute inset-x-0 top-0 h-36 bg-gradient-to-b to-transparent', toneWash(tone))}
      />
      <div className="relative px-5 pb-6 pt-6 sm:px-8 sm:pb-8 sm:pt-7">
        <StatusLabel tone={tone}>{label}</StatusLabel>
        <h2
          id="a2p-status-title"
          className="mt-5 text-balance !text-[1.625rem] font-medium leading-[1.15] tracking-[-0.015em] text-neutral-900 sm:!text-[2rem]"
          style={displayFont}
        >
          {title}
        </h2>
        {children && (
          <div className="mt-3 max-w-[62ch] space-y-2 text-pretty text-[15px] leading-relaxed text-neutral-600 [&_strong]:font-medium [&_strong]:text-neutral-900">
            {children}
          </div>
        )}
        {detail && <div className="mt-5">{detail}</div>}
        {actions && <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">{actions}</div>}
      </div>
      {facts && facts.length > 0 && (
        <FactGrid
          facts={facts}
          className="relative border-t border-neutral-200/80 bg-neutral-50/60 px-5 py-4 sm:px-8 sm:py-5"
        />
      )}
    </section>
  );
}

export function LoadingPanel() {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(SURFACE, 'flex min-h-[240px] items-center justify-center gap-3 text-sm text-neutral-500')}
    >
      <Loader2 aria-hidden className="size-4 animate-spin" />
      Loading your registration…
    </div>
  );
}
